import { makeAutoObservable } from "mobx";
import axios from 'axios';
import { getAPIUrl } from '../api/apiUtils';
import { formatRuDateTime } from '../utils/ruDateUtils';

class RegionsStore {
  // Состояние данных
  regions = [];
  isLoading = false;
  error = null;
  lastFetchTime = null;
  
  // Константы
  CACHE_TTL = 5 * 60 * 1000; // 5 минут в миллисекундах
  API_ENDPOINT = '/common/groups'; // Используем старый эндпоинт для совместимости

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  setRegions(regions) {
    this.regions = Array.isArray(regions) ? regions : [];
    this.lastFetchTime = Date.now();
    this.error = null;
  }

  setIsLoading(loading) {
    this.isLoading = loading;
  }

  setError(error) {
    this.error = error;
    this.isLoading = false;
  }

  // Computed values
  get hasData() {
    return this.regions.length > 0;
  }

  get isDataStale() {
    return !this.lastFetchTime || (Date.now() - this.lastFetchTime) > this.CACHE_TTL;
  }

  get shouldFetch() {
    return !this.hasData || this.isDataStale;
  }

  get allGroups() {
    return this.regions.flatMap(region => 
      region.groups ? region.groups.map(group => ({
        ...group,
        region: region.region,
        city: region.city
      })) : []
    );
  }

  get regionsList() {
    return this.regions.map(region => ({
      region: region.region,
      city: region.city,
      groupsCount: region.groups ? region.groups.length : 0
    }));
  }

  // Методы для работы с данными
  getGroupById(groupId) {
    const groupIdStr = String(groupId);
    return this.allGroups.find(group => String(group.group_id) === groupIdStr);
  }

  getGroupTitle(groupId) {
    const group = this.getGroupById(groupId);
    return group ? group.title : String(groupId);
  }

  getGroupsByRegion(regionName) {
    const region = this.regions.find(r => r.region === regionName);
    return region ? (region.groups || []) : [];
  }

  getGroupsByCity(cityName) {
    const region = this.regions.find(r => r.city === cityName);
    return region ? (region.groups || []) : [];
  }

  searchGroups(searchTerm) {
    if (!searchTerm) return this.allGroups;
    
    const term = searchTerm.toLowerCase();
    return this.allGroups.filter(group => 
      group.title.toLowerCase().includes(term) ||
      String(group.group_id).toLowerCase().includes(term) ||
      group.region.toLowerCase().includes(term) ||
      group.city.toLowerCase().includes(term)
    );
  }

  // Основной метод загрузки данных
  async fetchRegions(forceRefresh = false) {
    // Если данные актуальны и не требуется принудительное обновление
    if (!forceRefresh && !this.shouldFetch) {
      return this.regions;
    }

    // Если уже загружаем, не делаем повторный запрос
    if (this.isLoading) {
      return this.regions;
    }

    this.setIsLoading(true);
    this.setError(null);

    try {
      const response = await axios.get(`${getAPIUrl()}${this.API_ENDPOINT}`);
      
      if (Array.isArray(response.data)) {
        this.setRegions(response.data);
        return response.data;
      } else {
        throw new Error('Неверный формат данных от сервера');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Ошибка при загрузке регионов';
      this.setError(errorMessage);
      
      // Если это первая загрузка и произошла ошибка, возвращаем пустой массив
      if (!this.hasData) {
        this.setRegions([]);
      }
      
      throw error;
    } finally {
      this.setIsLoading(false);
    }
  }

  // Метод для принудительного обновления данных
  async refreshRegions() {
    return this.fetchRegions(true);
  }

  // Метод для очистки кэша
  clearCache() {
    this.lastFetchTime = null;
  }

  // Метод для сброса состояния
  reset() {
    this.regions = [];
    this.isLoading = false;
    this.error = null;
    this.lastFetchTime = null;
  }

  // Валидация структуры данных
  validateRegionData(data) {
    if (!Array.isArray(data)) return false;
    
    return data.every(region => 
      region && 
      typeof region.region === 'string' &&
      typeof region.city === 'string' &&
      Array.isArray(region.groups)
    );
  }

  // Метод для получения статистики
  getStats() {
    const totalGroups = this.allGroups.length;
    const totalRegions = this.regions.length;
    const regionsWithGroups = this.regions.filter(r => r.groups && r.groups.length > 0).length;
    
    return {
      totalGroups,
      totalRegions,
      regionsWithGroups,
      averageGroupsPerRegion: totalRegions > 0 ? (totalGroups / totalRegions).toFixed(1) : 0,
      lastUpdate: this.lastFetchTime ? formatRuDateTime(this.lastFetchTime) : 'Никогда'
    };
  }
}

export default new RegionsStore();
